'use client';

import { useEffect, useRef, useState } from 'react';

/** Scene units → percentage of the wall layer (shared by the scene layers). */
export const pct = (v, total) => `${(v / total) * 100}%`;

/**
 * A spot icon in a scene, positioned in viewBox percentages by the parent:
 * the symbol's watercolour mid when the solver found one (`icon.art`,
 * TT-1337), else its symbol-library SVG, fetched through the injected image
 * client, recolored to one ink, guide/rule paths hidden.
 */
export default function SceneIcon({ icon, viewBox, imageClient, delay = 0 }) {
  const art = icon.art;
  return (
    <div
      data-sid={icon.id}
      className="scene-icon scene-icon-in"
      style={{
        position: 'absolute',
        left: pct(icon.x, viewBox.width),
        top: pct(icon.y, viewBox.height),
        width: pct(icon.w, viewBox.width),
        height: pct(icon.h, viewBox.height),
        pointerEvents: 'none',
        animationDelay: delay ? `${delay}ms` : undefined,
      }}
    >
      {art ? (
        // eslint-disable-next-line -- the symbol's watercolour mid (no-img-element; named rules break tapp's CRA lint)
        <img
          src={art.url}
          // no crossOrigin, as SceneCanvas draws its art: when the plain URL's
          // CORS read fails, sceneAssets reads a copy instead, and a CORS <img>
          // of that URL would draw broken
          alt=""
          style={{
            position: 'absolute',
            left: pct(art.x - icon.x, icon.w),
            top: pct(art.y - icon.y, icon.h),
            width: pct(art.w, icon.w),
            height: pct(art.h, icon.h),
            maxWidth: 'none',
          }}
        />
      ) : (
        <LineIcon symbolId={icon.symbolId} color={icon.color} imageClient={imageClient} />
      )}
    </div>
  );
}

/** The symbol-library SVG, filling its icon's box in one ink. */
function LineIcon({ symbolId, color, imageClient }) {
  const hostRef = useRef(null);
  const [svg, setSvg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!imageClient) return undefined;
    imageClient.fetchSymbolImage(symbolId).then((info) => {
      if (!cancelled) setSvg(info?.svg || null);
    });
    return () => {
      cancelled = true;
    };
  }, [symbolId, imageClient]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    if (!svg) {
      host.innerHTML = ''; // symbol changed or fetch came back empty: drop the stale art
      return;
    }
    host.innerHTML = svg;
    const el = host.querySelector('svg');
    if (!el) return;
    el.setAttribute('width', '100%');
    el.setAttribute('height', '100%');
    el.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    el.querySelectorAll('style').forEach((s) => s.remove());
    el.querySelectorAll('path, line, polyline, polygon, rect, circle, ellipse').forEach((n) => {
      if (n.id && /^(rulepath|textpath)/i.test(n.id)) {
        n.style.visibility = 'hidden';
        return;
      }
      if (n.getAttribute('stroke') !== 'none') n.style.setProperty('stroke', color, 'important');
      const f = n.getAttribute('fill');
      if (f && f !== 'none') n.style.setProperty('fill', color, 'important');
    });
  }, [svg, color]);

  return <div ref={hostRef} style={{ width: '100%', height: '100%' }} />;
}
